/**
 * spatialAdvisor — live clearance + adjacency advice during drag.
 *
 * The 3D scene calls this on every pointer-move while a SKU is being
 * dragged. It walks the typed clearance thresholds in
 * codegen.json#statutoryThresholds and the room geometry to surface
 * the agentic tip ("0.42 m to north wall · NKBA G17 needs 0.6 m").
 *
 * Persona: Neeraj Bhatia (spatial-fit-check). No hardcoded numbers —
 * every clearance threshold comes from the ontology, every reason
 * carries its citation.
 */
import codegen from "../../../../docs/ontology/codegen.json";

export type AdviceTone = "ok" | "warn" | "block";

export interface SpatialAdvice {
  readonly tone: AdviceTone;
  readonly title: string;
  readonly detail: string;
  readonly citation: string;
}

interface StatutoryThreshold {
  id: string;
  kind?: string;
  valueM?: number;
  valueSqM?: number;
  citation: string;
  label: string;
  triggerCategory?: string | null;
}

interface AdviseArgs {
  /** SKU's category (from typed attrs). Drives which thresholds apply. */
  readonly category: string;
  /** SKU half-width and half-depth in metres (size[0]/2, size[2]/2). */
  readonly halfW: number;
  readonly halfD: number;
  /** Drag position in metres, relative to the room origin. */
  readonly xRel: number;
  readonly zRel: number;
  /** Room width / depth in metres. */
  readonly roomW: number;
  readonly roomD: number;
  /** Other SKUs already placed in the room — drop the dragged one. */
  readonly siblings: ReadonlyArray<{
    readonly id: string;
    readonly label: string;
    readonly xRel: number;
    readonly zRel: number;
    readonly halfW: number;
    readonly halfD: number;
  }>;
  /** Door openings on each wall — clearance must clear the swing zone. */
  readonly doorXRanges?: ReadonlyArray<{ wall: "north" | "south"; startM: number; widthM: number }>;
}

const THRESHOLDS: ReadonlyArray<StatutoryThreshold> = (codegen.statutoryThresholds ?? []) as ReadonlyArray<StatutoryThreshold>;
function threshold(kind: string): StatutoryThreshold | undefined {
  return THRESHOLDS.find((r) => r.kind === kind);
}

/** Walk every typed clearance + adjacency for the dragged SKU.
 *  Returns one or more agentic tips, sorted worst-first. */
export function adviseDragPosition(args: AdviseArgs): ReadonlyArray<SpatialAdvice> {
  const out: SpatialAdvice[] = [];
  const minWalkway = threshold("circulationReserveSmall");   // residential walkway 0.9 m
  const turnRadius = threshold("circulationReserveLarge");   // ANSI A117.1 1.5 m
  const minWalkwayM = minWalkway?.valueM ?? 0.9;

  // Distance from SKU edges to each room wall.
  const distNorth = args.zRel - args.halfD;
  const distSouth = args.roomD - (args.zRel + args.halfD);
  const distWest  = args.xRel - args.halfW;
  const distEast  = args.roomW - (args.xRel + args.halfW);
  const wallDistances = [
    { wall: "north", d: distNorth },
    { wall: "south", d: distSouth },
    { wall: "east",  d: distEast  },
    { wall: "west",  d: distWest  },
  ] as const;

  for (const w of wallDistances) {
    if (w.d < 0) {
      out.push({
        tone: "block",
        title: `${args.category} clips the ${w.wall} wall`,
        detail: `Edge overlap of ${Math.abs(w.d).toFixed(2)} m. Move inward.`,
        citation: minWalkway?.citation ?? "internal/wall-clip",
      });
    } else if (w.d < minWalkwayM) {
      out.push({
        tone: "warn",
        title: `${w.d.toFixed(2)} m to ${w.wall} wall`,
        detail: `Below the ${minWalkwayM.toFixed(2)} m residential walkway minimum (${minWalkway?.label ?? "walkway"}).`,
        citation: minWalkway?.citation ?? "NBC-2016-residential-walkway",
      });
    }
  }

  // Distance to siblings (other SKUs in same room).
  for (const sib of args.siblings) {
    const gapX = Math.abs(args.xRel - sib.xRel) - (args.halfW + sib.halfW);
    const gapZ = Math.abs(args.zRel - sib.zRel) - (args.halfD + sib.halfD);
    const gap = Math.max(gapX, gapZ); // axis-aligned bounding-box gap
    if (gap < 0) {
      out.push({
        tone: "block",
        title: `Overlapping ${sib.label}`,
        detail: `Bounding boxes intersect by ${Math.abs(gap).toFixed(2)} m. Separate them.`,
        citation: "internal/sku-collision",
      });
    } else if (gap < minWalkwayM) {
      out.push({
        tone: "warn",
        title: `${gap.toFixed(2)} m to ${sib.label}`,
        detail: `Below ${minWalkwayM.toFixed(2)} m walkway. Tight for circulation.`,
        citation: minWalkway?.citation ?? "NBC-2016-residential-walkway",
      });
    }
  }

  // Door swing zones — never park furniture in front of an open door.
  for (const dr of args.doorXRanges ?? []) {
    const inDoorX = args.xRel + args.halfW > dr.startM && args.xRel - args.halfW < dr.startM + dr.widthM;
    const closeToWall = dr.wall === "north" ? distNorth < dr.widthM : distSouth < dr.widthM;
    if (inDoorX && closeToWall) {
      out.push({
        tone: "block",
        title: `Blocks the ${dr.wall} door swing`,
        detail: `${args.category} sits inside the door's ${dr.widthM.toFixed(2)} m swing arc.`,
        citation: "ANSI-A117.1-404-door-clearance",
      });
    }
  }

  // Wheelchair turn-radius check (informational at this stage).
  if (turnRadius && out.length === 0) {
    const minWallDist = Math.min(distNorth, distSouth, distEast, distWest);
    if (minWallDist >= turnRadius.valueM!) {
      out.push({
        tone: "ok",
        title: "Clear",
        detail: `${minWallDist.toFixed(2)} m to nearest wall — clears ANSI A117.1 ${turnRadius.valueM} m turn radius.`,
        citation: turnRadius.citation,
      });
    }
  }

  // Sort worst-first so the cockpit can show the most-severe tip first.
  return out.sort((a, b) => weight(b.tone) - weight(a.tone));
}

function weight(t: AdviceTone): number {
  return t === "block" ? 2 : t === "warn" ? 1 : 0;
}
